/**
 * 車輛選擇頁面
 * 顯示公司所有車輛，司機選擇車輛開始任務
 * 依上次使用時間排序（最近使用的在前）
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiff } from '@/contexts/LiffContext'
import { getDriver } from '@/services/driverService'
import {
  getCompanyVehicles,
  getVehicleHistory,
  getSortedVehicles,
} from '@/services/vehicleService'
import type { Driver, Vehicle, VehicleHistory } from '@/types'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import StarIcon from '@mui/icons-material/Star'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const VehicleSelectionPage: React.FC = () => {
  const navigate = useNavigate()
  const { lineUser, idToken } = useLiff()

  // ========== State ==========
  const [driver, setDriver] = useState<Driver | null>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [vehicleHistory, setVehicleHistory] = useState<VehicleHistory[]>([])
  const [loading, setLoading] = useState(true)
  // 契約車司機的綁定車輛目前有別人在上工 → 不能上工，顯示提示
  const [contractBlocked, setContractBlocked] = useState<{ vehicleNo: string; driverName: string } | null>(null)

  // ========== 載入資料 ==========
  useEffect(() => {
    const loadData = async () => {
      if (!lineUser) return

      try {
        // 載入司機資料
        const driverData = await getDriver(lineUser.userId)
        if (!driverData) {
          navigate('/binding', { replace: true })
          return
        }
        setDriver(driverData)

        // 載入公司車輛
        const companyVehicles = await getCompanyVehicles(driverData.companyName, idToken ?? undefined)

        // 載入司機車輛使用歷史
        const historyData = await getVehicleHistory(driverData.driverId, idToken ?? undefined)
        setVehicleHistory(historyData)

        // 排序車輛：依上次使用時間排序（最近使用的在前）
        const sortedVehicles = getSortedVehicles(companyVehicles, historyData)
        setVehicles(sortedVehicles)

        // 契約車司機（company_type=60）且司機檔有綁定車輛：不選車，直接帶那台車進確認頁。
        // 那台車有別人在上工 → 擋下（後端接班也會再擋一次）。沒設綁定車輛 → 照舊自己選。
        if (driverData.isContractCompany && driverData.contractVehicleNo) {
          const norm = (v: string) => v.replace(/-/g, '').toUpperCase()
          const bound = companyVehicles.find((v) => norm(v.vehicleNo) === norm(driverData.contractVehicleNo as string))
          if (bound) {
            if (bound.status === 'AVAILABLE') {
              navigate('/vehicle-confirm', { replace: true, state: { vehicle: bound } })
              return
            }
            setContractBlocked({ vehicleNo: bound.vehicleNo, driverName: bound.currentDriverName || '其他司機' })
          }
        }
      } catch (error) {
        console.error('載入資料失敗:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [lineUser, navigate])

  // ========== 輔助函數 ==========
  const isFrequentVehicle = (vehicleId: number): boolean => {
    return vehicleHistory.some((h) => h.vehicleId === vehicleId)
  }

  // ========== Event Handlers ==========
  const handleSelectVehicle = (vehicle: Vehicle) => {
    // 導向車輛資訊確認頁面
    navigate('/vehicle-confirm', {
      state: {
        vehicle: vehicle,
      },
    })
  }

  const handleBack = () => {
    navigate('/home')
  }

  // ========== 載入中 ==========
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6B705C' }} />
        <Typography variant="body1" sx={{ mt: 2, color: '#6B705C' }}>
          載入中...
        </Typography>
      </Box>
    )
  }

  // ========== Render ==========
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        py: 3,
      }}
    >
      {/* 契約車綁定車輛被占用：不能上工 */}
      <Dialog open={contractBlocked !== null} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#6B705C', textAlign: 'center', pt: 3 }}>
          無法上工
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            您的綁定車輛 <strong>{contractBlocked?.vehicleNo}</strong> 目前由 <strong>{contractBlocked?.driverName}</strong> 上工中，
            請等對方交班後再上工。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" onClick={handleBack} sx={{ backgroundColor: '#6B705C', '&:hover': { backgroundColor: '#5a5f4d' } }}>
            返回首頁
          </Button>
        </DialogActions>
      </Dialog>
      <Container maxWidth="md">
        {/* 頂部返回按鈕 */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            mb: 2,
            color: '#6B705C',
            '&:hover': {
              backgroundColor: 'rgba(107, 112, 92, 0.08)',
            },
          }}
        >
          返回首頁
        </Button>

        {/* 頁面標題 */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #6B705C 0%, #A5A58D 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DirectionsCarIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                選擇車輛
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {driver?.companyName} - {vehicles.length} 輛車輛
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 車輛列表 - 雙列顯示車號 */}
        <Grid container spacing={2}>
          {vehicles.map((vehicle) => {
            const isAvailable = vehicle.status === 'AVAILABLE'
            const isFrequent = isFrequentVehicle(vehicle.vehicleId)

            return (
              <Grid item xs={6} key={vehicle.vehicleId}>
                <Paper
                  elevation={isFrequent ? 4 : 2}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3,
                    border: isFrequent ? '2px solid #FFB84D' : 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: isAvailable ? 'translateY(-4px)' : 'none',
                      boxShadow: isAvailable ? 6 : 2,
                    },
                  }}
                >
                  {/* 常用車輛標記 */}
                  {isFrequent && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        background: 'linear-gradient(135deg, #FFB84D 0%, #FFA726 100%)',
                        color: 'white',
                        px: 1.5,
                        py: 0.3,
                        borderBottomLeftRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.3,
                      }}
                    >
                      <StarIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        常用
                      </Typography>
                    </Box>
                  )}

                  <Button
                    fullWidth
                    onClick={() => handleSelectVehicle(vehicle)}
                    disabled={!isAvailable}
                    sx={{
                      p: 2.5,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 100,
                      textTransform: 'none',
                      color: 'inherit',
                      '&:disabled': {
                        color: 'text.disabled',
                        opacity: 0.5,
                      },
                    }}
                  >
                    {/* 車牌號碼 - 大字顯示 */}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: isAvailable ? '#6B705C' : 'text.disabled',
                        letterSpacing: 1,
                      }}
                    >
                      {vehicle.vehicleNo}
                    </Typography>
                  </Button>
                </Paper>
              </Grid>
            )
          })}
        </Grid>

        {/* 無車輛提示 */}
        {vehicles.length === 0 && (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              目前沒有可用的車輛
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  )
}

export default VehicleSelectionPage
